import React, { memo } from 'react';
import type { Node, NodeProps } from '@xyflow/react';
import { WorkflowNode } from './components/node';
import type { WorkflowNodeViewData } from './components/node';
import {
  resolveGraphNodeRenderableNodeByViewData,
  useWorkflowRenderCatalog,
} from './workflow-render-catalog';
import { WORKFLOW_SELECTED_CLASS } from './components/node-selection';
import type { FanOutGroupSummary } from './workflow-run-fan-out-group';
import { WorkflowRunFanOutCard } from './workflow-run-fan-out-card';

export const WORKFLOW_RUN_FLOW_NODE_TYPE = 'workflowRunNode' as const;

/** Flow-node data for a run graph node: the node view data + its run status. */
export type WorkflowRunFlowNodeData = {
  node: WorkflowNodeViewData;
  status: string | null;
  /** Definition `run_node_id` (stable; React Flow `id` may include instance key). */
  definitionNodeId: string;
  /** Step instance key this card represents (`root` when not fanned out). */
  instanceKey: string;
  /**
   * Set when this card collapses a for-each fan-out: the iteration count and
   * status rollup it stands in for. `null`/absent for an ordinary single card.
   */
  fanOut?: FanOutGroupSummary | null;
};

/**
 * Read-only run-graph node. Prefers the editor's `renderFlowNode` (e.g.
 * {@link AgentNodeCard} for live agent titles) when registered; otherwise the
 * catalog `view` via {@link WorkflowNode}. Clicks are handled by the canvas
 * (`onNodeClick`) which opens the node's step view.
 */
export const WorkflowRunFlowNode = memo(function WorkflowRunFlowNode({
  data,
  selected,
}: NodeProps<Node<WorkflowRunFlowNodeData>>) {
  const nodeData = data as WorkflowRunFlowNodeData;
  const catalog = useWorkflowRenderCatalog();
  const entry = resolveGraphNodeRenderableNodeByViewData(
    catalog,
    nodeData.node
  );
  const className = selected === true ? WORKFLOW_SELECTED_CLASS : undefined;

  // A collapsed fan-out renders as a stacked "deck" card with a count + status
  // rollup instead of the plain node card, so 300 iterations read as one node.
  if (nodeData.fanOut !== undefined && nodeData.fanOut !== null) {
    return (
      <div className="group relative w-fit cursor-pointer">
        <WorkflowRunFanOutCard
          node={nodeData.node}
          fanOut={nodeData.fanOut}
          className={className}
        />
      </div>
    );
  }

  const card =
    entry.renderFlowNode !== undefined ? (
      entry.renderFlowNode({
        node: nodeData.node,
        selected,
        className,
        viewModel: null,
        status: nodeData.status,
      })
    ) : (
      <WorkflowNode
        node={nodeData.node}
        className={className}
        status={nodeData.status}
      />
    );

  return <div className="group relative w-fit cursor-pointer">{card}</div>;
});
